import type { PhotoTintLayout } from '../helpers/photo-tint'

interface CropPhotoTintProps {
  tint: PhotoTintLayout
  /** Live frame origin (doc space). */
  ox: number
  oy: number
  /** The shape prop's own origin — where its stored outline path is anchored. */
  shapeX: number
  shapeY: number
  previewUrl: string
}

/**
 * Full photo + outside-path tint (see `resolvePhotoTintLayout`). The mask's white region is the
 * photo's OWN rendered rect (same transform as the image below, not the geometric frame) —
 * cover-fit always sizes the photo to be at least as large as the frame, so this never clips the
 * photo short; it can only ever be equal to or larger than the frame. The black path punched
 * into it (the shape's own outline — a real custom path, or a synthesized plain rectangle for a
 * shape with no stored path — shifted into absolute doc space) hides that same region, letting
 * the crisp path/frame-clipped photo from shape-renderer show through untouched underneath —
 * this layer never draws anything there, so z-order relative to IT doesn't matter, only relative
 * to the frame/handles drawn after it.
 */
export function CropPhotoTint({ tint, ox, oy, shapeX, shapeY, previewUrl }: CropPhotoTintProps) {
  const { shapePathD, tintImageWidth, tintImageHeight, tintImageTransform, tintMaskId } = tint

  return (
    <>
      <defs>
        <mask id={tintMaskId}>
          <g transform={`translate(${ox}, ${oy})`}>
            <g transform={tintImageTransform}>
              <rect x={0} y={0} width={tintImageWidth} height={tintImageHeight} fill="white" />
            </g>
          </g>
          <g transform={`translate(${shapeX}, ${shapeY})`}>
            <path d={shapePathD} fill="black" />
          </g>
        </mask>
      </defs>
      <g mask={`url(#${tintMaskId})`} pointerEvents="none">
        <g transform={`translate(${ox}, ${oy})`}>
          <g transform={tintImageTransform}>
            <image
              href={previewUrl}
              x={0}
              y={0}
              width={tintImageWidth}
              height={tintImageHeight}
              preserveAspectRatio="xMidYMid slice"
            />
            {/* Same white-wash-at-75%-opacity tint the crop dim overlay uses
              (center-canvas/components/edit-mode-dim-overlay.tsx), reused here for visual
              consistency. Sized/transformed identically to the image above so it exactly covers
              the photo, including wherever it extends past the shape's own frame. */}
            <rect
              x={0}
              y={0}
              width={tintImageWidth}
              height={tintImageHeight}
              fill="#ffffff"
              opacity={0.75}
            />
          </g>
        </g>
      </g>
    </>
  )
}
